import { ReactElement, useState } from 'react';

import { Component as DeadlineIcon } from 'icons/icon-circle-lime--deadline.svg';
import { Component as EditIcon } from 'icons/icon-ui-edit.svg';
import { Component as ChefsChoiceIcon } from 'icons/icon-circle-lime--knife.svg';
import { Component as HighProteinIcon } from 'icons/icon-circle-lime--dumbbell.svg';
import { Component as HighFiberIcon } from 'icons/icon-circle-lime--leaf.svg';
import { Component as GlutenFreeIcon } from 'icons/icon-circle-lime--gluten-free.svg';
import { Component as JumpStartIcon } from 'icons/icon-circle-lime--jumpstart.svg';
import { Component as LessPrepIcon } from 'icons/icon-circle-lime--clock.svg';

import { get } from 'utils/network';
import { ApiRoute } from 'config';
import useCurrentUser from 'hooks/useCurrentUser';
import useAsyncEffect from 'hooks/useAsyncEffect';
import Plan from 'models/Plan';
import IPlanInfo from 'models/IPlanInfo';
import UpdatePlan from '../../UpdatePlan';

type Props = {
  nextSkipDeadline: string
};

function UpcomingDeliveriesWidget({ nextSkipDeadline }:Props): ReactElement {
  const [isPlanInitialized, setPlanInitialized] = useState(Plan.isInitialized);
  const [showUpdatePlanModal, setShowUpdatePlanModal] = useState(false);

  const { currentUser } = useCurrentUser();
  const { canEditPlan } = currentUser;
  const subscription = currentUser?.subscription;
  const plan:Plan = new Plan(subscription.plan, subscription.extras, subscription.is_diversified);

  const planType = plan?.type;
  // No preference is displayed as chefs choice
  const preference = plan.mealPreferences === 'No preference' ? "Chef's Choice" : plan.mealPreferences;

  const planTitle = () => {
    switch (planType) {
      case 0:
        return 'Meal Kit';
      case 1:
      case 2:
        return 'Ready-To-Eat';
      case 3:
        return 'Mix and Match';
      default:
        return '';
    }
  };

  const planSubtitle = () => {
    if (planType === 0) {
      return `${plan.servings}-Serving Plan`;
    }
    return 'Plan';
  };

  useAsyncEffect(async (isMounted) => {
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
      if (!response.parsedBody) {
        return;
      }
      Plan.initialize(response.parsedBody);
      if (isMounted) setPlanInitialized(true);
    }
  }, []);

  const displayPreferenceImage = (pref: string) => {
    switch (pref) {
      case "Chef's Choice":
      case 'No preference':
        return <ChefsChoiceIcon height={36} width={36} />;
      case 'High Protein':
        return <HighProteinIcon height={36} width={36} />;
      case 'High Fiber':
        return <HighFiberIcon height={36} width={36} />;
      case 'Gluten-Free':
        return <GlutenFreeIcon height={36} width={36} />;
      case 'Less Prep':
        return <LessPrepIcon height={36} width={36} />;
      case 'Jumpstart':
        return <JumpStartIcon height={36} width={36} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="c-upcoming-menu-widget">
        <div className="c-upcoming-menu-widget__left-block">
          <div className="c-upcoming-menu-widget__plan-block">
            <div className={`c-upcoming-menu-widget__plan-image c-upcoming-menu-widget__plan-image-${planType}`} />
            <div className="c-upcoming-menu-widget__title" style={{ fontSize: '18px' }}>{planTitle()}</div>
            <div className="c-upcoming-menu-widget__subtitle">{planSubtitle()}</div>
          </div>

          <div className="c-upcoming-menu-widget__preference-block">
            <div className="c-upcoming-menu-widget__preference-icons">
              {displayPreferenceImage(preference)}
              {canEditPlan && (
              <div
                className="c-upcoming-menu-widget__edit-icon"
                role="button"
                tabIndex={0}
                onClick={() => setShowUpdatePlanModal(true)}
                onKeyUp={(e) => e.key === 'Enter' && setShowUpdatePlanModal(true)}
              >
                <EditIcon />
              </div>
              )}
            </div>
            <div className="c-upcoming-menu-widget__title ">
              {preference}
            </div>
            <div className="c-upcoming-menu-widget__subtitle">Preference</div>
          </div>
        </div>

        <div className="c-upcoming-menu-widget__right-block">
          <div className="c-upcoming-menu-widget__deadline-icon">
            <DeadlineIcon height={36} width={36} />
          </div>
          <div className="c-upcoming-menu-widget__title">{nextSkipDeadline}</div>
          <div className="c-upcoming-menu-widget__subtitle">Next Skip Deadline</div>
        </div>
      </div>

      {isPlanInitialized && (
      <UpdatePlan
        show={showUpdatePlanModal}
        existingPlan={plan}
        onClose={() => setShowUpdatePlanModal(false)}
      />
      )}
    </>
  );
}

export default UpcomingDeliveriesWidget;
